// Global Instructions Rule Applied!

/**
 * Shapes a Downloads-screen report grid (Schedule of Investments, Cash Flows)
 * into the payload the branded Excel builder expects.
 *
 * The rows are built exactly as the plain export builds them — same cell
 * resolver, same total-row handling — so the workbook reads in the order and
 * with the columns the user set on screen. What is new is that each column
 * carries a FORMAT, because the branded sheet applies the firm's accounting
 * number conventions per type rather than dumping bare numbers.
 */
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import type { BrandedExcelColumn } from '../../../core/services/utilitiesService.ts'
import { resolveReportingGridExportCellValue } from '../../../state_manager/utils/reportingGridExcelExport.ts'
import { recordHasTotalLabel, stripTotalWordFromString } from '../../../core/utils/recordRowBold.ts'

/**
 * IRR is cfg-typed `number` but stored as a fractional rate and displayed as
 * a percentage, and its export value comes from the raw `record.irr` (see the
 * irr_fmt special case in resolveReportingGridExportCellValue) — so its
 * branded type is keyed here rather than derived from cfg.
 */
const PERCENT_VALUE_KEYS = new Set(['irr_fmt'])

/**
 * The branded type comes from the cfg column type (`cfg.reporting_column_types`
 * via fieldType): `ratio` for MOIC-style multiples, `currency` for whole-unit
 * money, `date` for dates, everything else text.
 */
export function resolveBrandedColumnType(column: ViewConfigColumn): BrandedExcelColumn['type'] {
  if (PERCENT_VALUE_KEYS.has(column.valueKey)) return 'percent'
  if (column.fieldType === 'ratio') return 'ratio'
  if (column.fieldType === 'date') return 'date'
  if (column.fieldType === 'currency') return 'currency'
  return 'text'
}

export interface BrandedExportPayload {
  data: Record<string, unknown>[]
  columns: BrandedExcelColumn[]
  totalRowIndexes: number[]
}

/**
 * Build the rows, typed column specs, and the positions of the total rows.
 *
 * Total rows are collected in the same pass that writes them, so their indexes
 * always refer to the array actually sent — the builder must not have to
 * re-derive which rows are totals.
 */
export function buildBrandedExportPayload(
  columns: ViewConfigColumn[],
  records: Record<string, unknown>[]
): BrandedExportPayload {
  const columnSpecs: BrandedExcelColumn[] = columns.map((column) => ({
    key: column.columnTitle,
    title: column.columnTitle,
    type: resolveBrandedColumnType(column)
  }))

  const totalRowIndexes: number[] = []
  const data = records.map((record, rowIndex) => {
    const isTotalRow = recordHasTotalLabel(record)
    if (isTotalRow) totalRowIndexes.push(rowIndex)

    const row: Record<string, unknown> = {}
    columns.forEach((config, columnIndex) => {
      let value: unknown = resolveReportingGridExportCellValue(record, config)
      // "Total" belongs in the first column only; the grid shows it the same way.
      if (isTotalRow && typeof value === 'string' && columnIndex > 0) {
        value = stripTotalWordFromString(value)
      }
      row[config.columnTitle] = value === undefined ? null : value
    })
    return row
  })

  return { data, columns: columnSpecs, totalRowIndexes }
}

/** "As of June 30, 2026" — the dated line under the report title. */
export function buildAsOfLabel(formattedDate: string): string {
  return `As of ${formattedDate}`
}

/**
 * The selection line: which funds, broken out how. Reads as the masthead meta
 * of the printed reports — "All funds · By fund and vintage". Breakouts are an
 * SOI concept; reports without them (Cash Flows) omit the argument and get the
 * fund selection alone.
 */
export function buildDownloadsSubtitle(
  fundNames: string[],
  breakouts?: { byFund: boolean; byVintage: boolean; byTranche: boolean }
): string {
  const fundsLabel = fundNames.length > 0 ? fundNames.join(', ') : 'All funds'

  const enabled: string[] = []
  if (breakouts?.byFund) enabled.push('fund')
  if (breakouts?.byVintage) enabled.push('vintage')
  if (breakouts?.byTranche) enabled.push('tranche')
  if (enabled.length === 0) return fundsLabel

  const breakoutLabel =
    enabled.length === 1
      ? enabled[0]
      : `${enabled.slice(0, -1).join(', ')} and ${enabled[enabled.length - 1]}`

  return `${fundsLabel} · By ${breakoutLabel}`
}
